import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  GiftIcon,
  PlusIcon,
  XMarkIcon,
  ArrowRightIcon,
  TrashIcon,
  UserIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShareIcon,
  UsersIcon,
  CakeIcon,
  SparklesIcon,
  HeartIcon,
  CalendarIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import { useAuthStore } from '../../store/authStore';
import api from '../../api/client';
import { useTranslation } from 'react-i18next';
import MemberAvatar from '../../components/common/MemberAvatar';
import { useMarkCategoryRead } from '../../hooks/useMarkCategoryRead';

interface GiftList {
  id: string;
  family_id: string;
  owner_user_id: string | null;
  owner_member_id: string | null;
  name: string;
  description: string | null;
  occasion: string | null;
  occasion_date: string | null;
  is_public: boolean;
  owner_name: string;
  gift_count: number;
  reserved_count: number;
  is_own_list: boolean;
  created_at: string;
  is_shared?: boolean;
  source_family_name?: string | null;
}

interface LinkedFamily {
  id: string;
  family_id: string;
  family_name: string;
}

interface FamilyMember {
  id: string;
  user_id: string | null;
  first_name: string;
  last_name: string | null;
  nickname: string | null;
  is_managed: boolean;
  avatar_url: string | null;
}

interface CreateListForm {
  name: string;
  description: string;
  occasion: string;
  owner_type: 'self' | 'member';
  owner_member_id: string;
}

interface SpecialDate {
  id: string;
  user_id: string | null;
  family_member_id: string | null;
  date_type: string;
  date: string;
  name: string | null;
  is_recurring: boolean;
  person_name?: string;
}

interface OwnerGroup {
  id: string;
  name: string;
  isCurrentUser: boolean;
  isManaged: boolean;
  avatarUrl: string | null;
  lists: GiftList[];
  specialDates: SpecialDate[];
  userId?: string | null;
  memberId?: string | null;
}

// Ring icon for anniversaries
const RingIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="6" />
    <path d="M12 2v4" />
    <path d="M9 3l3 3 3-3" />
  </svg>
);

const specialDateIcons: Record<string, { icon: React.ComponentType<any>; color: string; emoji: string }> = {
  birthday: { icon: CakeIcon, color: 'text-pink-400', emoji: '🎂' },
  nameday: { icon: CalendarIcon, color: 'text-purple-400', emoji: '📅' },
  anniversary: { icon: RingIcon, color: 'text-amber-400', emoji: '💍' },
  wedding: { icon: RingIcon, color: 'text-amber-400', emoji: '💒' },
  other: { icon: SparklesIcon, color: 'text-blue-400', emoji: '✨' },
};

const formatSpecialDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const occasionLabels: Record<string, string> = {
  christmas: '🎄 Christmas',
  birthday: '🎂 Birthday',
  anniversary: '💍 Anniversary',
  hanukkah: '🕎 Hanukkah',
  nameday: '📛 Name Day',
  valentines: '❤️ Valentine\'s',
  mothers_day: '👩 Mother\'s Day',
  fathers_day: '👨 Father\'s Day',
  easter: '🐰 Easter',
  graduation: '🎓 Graduation',
  wedding: '💒 Wedding',
  other: '🎁 Other',
};

export default function GiftLists() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentFamily } = useFamilyStore();
  const { user } = useAuthStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['current-user', 'shared']));
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const [linkedFamilies, setLinkedFamilies] = useState<LinkedFamily[]>([]);
  const [loadingLinkedFamilies, setLoadingLinkedFamilies] = useState(false);
  const [showEditModal, setShowEditModal] = useState<GiftList | null>(null);
  
  // Mark gift notifications as read when visiting this page
  useMarkCategoryRead('gifts');
  const { t } = useTranslation();

  const { register, handleSubmit, reset, watch, setValue } = useForm<CreateListForm>({
    defaultValues: { occasion: '', owner_type: 'self', owner_member_id: '' }
  });

  const { register: registerEdit, handleSubmit: handleSubmitEdit, reset: resetEdit, watch: watchEdit, setValue: setValueEdit } = useForm<CreateListForm>({
    defaultValues: { occasion: '', owner_type: 'self', owner_member_id: '' }
  });

  const ownerType = watch('owner_type');

  // Fetch gift lists
  const { data: lists, isLoading } = useQuery({
    queryKey: ['giftLists', currentFamily?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      const response = await api.get(`/gifts/family/${currentFamily.id}`);
      return response.data as GiftList[];
    },
    enabled: !!currentFamily?.id,
  });

  // Fetch special dates for the family
  const { data: specialDates } = useQuery({
    queryKey: ['specialDates', currentFamily?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      const response = await api.get(`/gifts/special-dates/family/${currentFamily.id}`);
      return response.data as SpecialDate[];
    },
    enabled: !!currentFamily?.id,
  });

  // Fetch ALL family members (not just managed) for selection
  const { data: familyData } = useQuery({
    queryKey: ['familyDetail', currentFamily?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return null;
      const response = await api.get(`/families/${currentFamily.id}`);
      return response.data;
    },
    enabled: !!currentFamily?.id,
  });

  const members = familyData?.members as FamilyMember[] | undefined;

  // Group lists by owner - includes ALL family members even without wishlists
  const groupedLists = useMemo(() => {
    if (!members) return [];

    const groups: Map<string, OwnerGroup> = new Map();
    
    // First, create a group for the current user
    if (user) {
      const userDates = specialDates?.filter(sd => sd.user_id === user.id) || [];
      groups.set('current-user', {
        id: 'current-user',
        name: 'My Wishlists',
        isCurrentUser: true,
        isManaged: false,
        avatarUrl: user.avatar_url || null,
        lists: [],
        specialDates: userDates,
        userId: user.id,
      });
    }
    
    // Create groups for all other family members
    for (const member of members) {
      // Skip if it's the current user (already added)
      if (member.user_id === user?.id) continue;
      
      const memberName = member.nickname || 
        `${member.first_name || ''} ${member.last_name || ''}`.trim() || 
        'Unknown Member';
      
      const memberKey = member.user_id || member.id;
      const memberDates = specialDates?.filter(sd => 
        (member.user_id && sd.user_id === member.user_id) ||
        sd.family_member_id === member.id
      ) || [];
      
      groups.set(memberKey, {
        id: memberKey,
        name: memberName,
        isCurrentUser: false,
        isManaged: member.is_managed,
        avatarUrl: member.avatar_url || null,
        lists: [],
        specialDates: memberDates,
        userId: member.user_id,
        memberId: member.id,
      });
    }
    
    // Create a group for shared lists from linked families
    groups.set('shared', {
      id: 'shared',
      name: 'Shared from Linked Families',
      isCurrentUser: false,
      isManaged: false,
      avatarUrl: null,
      lists: [],
      specialDates: [],
    });

    // Process each list and add to appropriate group
    if (lists) {
      for (const list of lists) {
        // Check if this is a shared list first
        if (list.is_shared) {
          const group = groups.get('shared');
          if (group) {
            group.lists.push(list);
          }
        } else if (list.is_own_list) {
          // Add to current user's group
          const group = groups.get('current-user');
          if (group) {
            group.lists.push(list);
          }
        } else {
          // Find the group for this owner
          const groupKey = list.owner_user_id || list.owner_member_id || 'unknown';
          const group = groups.get(groupKey);
          if (group) {
            group.lists.push(list);
          }
        }
      }
    }

    // Convert to array and sort (current user first, shared last, then alphabetically)
    // Include ALL family members - only filter out empty "shared" group
    return Array.from(groups.values())
      .filter(g => g.id !== 'shared' || g.lists.length > 0)
      .sort((a, b) => {
        if (a.isCurrentUser) return -1;
        if (b.isCurrentUser) return 1;
        if (a.id === 'shared') return 1;
        if (b.id === 'shared') return -1;
        return a.name.localeCompare(b.name);
      });
  }, [lists, members, user, specialDates]);
  
  const loadLinkedFamilies = async () => {
    if (!currentFamily?.id) return;
    setLoadingLinkedFamilies(true);
    try {
      const response = await api.get(`/family-links/${currentFamily.id}/linked`);
      setLinkedFamilies(response.data || []);
    } catch (error) {
      console.error('Failed to load linked families:', error);
    } finally {
      setLoadingLinkedFamilies(false);
    }
  };
  
  const handleOpenShareModal = async (listId: string) => {
    await loadLinkedFamilies();
    setShowShareModal(listId);
  };
  
  const handleShareList = async (targetFamilyId: string) => {
    if (!currentFamily?.id || !showShareModal) return;
    try {
      await api.post(`/family-links/${currentFamily.id}/share`, {
        resource_type: 'gift_list',
        resource_id: showShareModal,
        target_family_id: targetFamilyId,
      });
      toast.success('Wishlist shared successfully!');
      setShowShareModal(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to share wishlist');
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: CreateListForm) => {
      if (!currentFamily?.id) throw new Error('No family selected');
      const payload: any = {
        name: data.name,
        description: data.description || null,
        occasion: data.occasion || null,
      };
      if (data.owner_type === 'member' && data.owner_member_id) {
        payload.owner_member_id = data.owner_member_id;
      }
      const response = await api.post(`/gifts/family/${currentFamily.id}`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['giftLists'] });
      toast.success('Gift list created!');
      setShowCreateModal(false);
      reset();
      navigate(`/gifts/${data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create list');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (listId: string) => {
      await api.delete(`/gifts/${listId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftLists'] });
      toast.success('List deleted');
    },
    onError: () => toast.error('Failed to delete list'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ listId, data }: { listId: string; data: Partial<CreateListForm> }) => {
      const payload: any = {
        name: data.name,
        description: data.description || null,
        occasion: data.occasion || null,
      };
      const response = await api.patch(`/gifts/${listId}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giftLists'] });
      toast.success('Wishlist updated!');
      setShowEditModal(null);
      resetEdit();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update list');
    },
  });

  const handleOpenEdit = (list: GiftList) => {
    setValueEdit('name', list.name);
    setValueEdit('description', list.description || '');
    setValueEdit('occasion', list.occasion || '');
    setShowEditModal(list);
  };

  const onSubmitEdit = (data: CreateListForm) => {
    if (!showEditModal) return;
    updateMutation.mutate({ listId: showEditModal.id, data });
  };

  const onSubmit = (data: CreateListForm) => {
    createMutation.mutate(data);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Get members that can have wishlists created for them (managed members only for now)
  const managedMembers = members?.filter(m => m.is_managed) || [];

  if (!currentFamily) {
    return (
      <div className="page-content">
        <div className="card text-center py-10">
          <GiftIcon className="w-12 h-12 mx-auto text-surface-500 mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">No Family Selected</h2>
          <p className="text-surface-400 text-sm mb-4">Select a family to view gift lists</p>
          <Link to="/families" className="btn-primary inline-block text-sm py-2 px-4">
            Go to Families
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-white">Gift Wishlists</h1>
          <p className="text-surface-400 text-xs">Manage gift ideas for the family</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
          <PlusIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">New List</span>
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : lists?.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-10"
        >
          <GiftIcon className="w-12 h-12 mx-auto text-surface-500 mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">No Gift Lists Yet</h2>
          <p className="text-surface-400 text-sm mb-4">Create your first wishlist to share with family</p>
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
            <PlusIcon className="w-3.5 h-3.5" />
            Create Wishlist
          </button>
        </motion.div>
      ) : (
        <div className="space-y-2">
          {groupedLists.map((group) => (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card overflow-hidden !p-0"
            >
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-surface-700/50 transition-colors ${
                  group.id === 'shared' ? 'bg-accent-500/5' : ''
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {/* Avatar/Icon */}
                  {group.id === 'shared' ? (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center">
                      <UsersIcon className="w-4 h-4 text-white" />
                    </div>
                  ) : (
                    <MemberAvatar
                      avatarUrl={group.avatarUrl}
                      name={group.name}
                      role={group.isManaged ? 'child' : group.isCurrentUser ? 'owner' : 'member'}
                      size="sm"
                    />
                  )}
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-white">
                      {group.name}
                      {group.isManaged && (
                        <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                          Child
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-surface-400">
                      {group.lists.length} {group.lists.length === 1 ? 'wishlist' : 'wishlists'}
                      {group.specialDates.length > 0 && (
                        <span className="ml-1.5 text-surface-500">
                          • {group.specialDates.length} {group.specialDates.length === 1 ? 'date' : 'dates'}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {expandedGroups.has(group.id) ? (
                  <ChevronDownIcon className="w-4 h-4 text-surface-400" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-surface-400" />
                )}
              </button>

              {/* Group Lists */}
              <AnimatePresence>
                {expandedGroups.has(group.id) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-surface-700 px-3 py-2 space-y-1.5">
                      {/* Special Dates */}
                      {group.specialDates.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {group.specialDates.map((sd) => {
                            const dateInfo = specialDateIcons[sd.date_type] || specialDateIcons.other;
                            const DateIcon = dateInfo.icon;
                            return (
                              <div
                                key={sd.id}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-surface-800 border border-surface-700"
                                title={sd.name || sd.date_type}
                              >
                                <DateIcon className={`w-3.5 h-3.5 ${dateInfo.color}`} />
                                <span className="text-xs text-surface-300">
                                  {formatSpecialDate(sd.date)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Wishlists */}
                      {group.lists.length === 0 && group.specialDates.length > 0 && (
                        <p className="text-xs text-surface-500 italic py-1">No wishlists yet</p>
                      )}
                      {group.lists.map((list) => (
                        <Link
                          key={list.id}
                          to={`/gifts/${list.id}`}
                          className={`block px-2.5 py-2 rounded-lg bg-surface-700/50 hover:bg-surface-700 transition-all group ${
                            list.is_shared ? 'border-l-2 border-l-accent-500' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5 flex-1 min-w-0">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                list.is_shared
                                  ? 'bg-gradient-to-br from-accent-500/20 to-primary-500/20'
                                  : 'bg-gradient-to-br from-pink-500/20 to-purple-600/20'
                              }`}>
                                <GiftIcon className={`w-4 h-4 ${list.is_shared ? 'text-accent-400' : 'text-pink-400'}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <h4 className="text-sm font-medium text-white truncate group-hover:text-primary-400 transition-colors">
                                    {list.name}
                                  </h4>
                                  {list.is_shared && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-500/20 text-accent-300 flex items-center gap-0.5">
                                      <UsersIcon className="w-2.5 h-2.5" />
                                      {list.source_family_name || 'Shared'}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  {list.occasion && (
                                    <span className="text-surface-400">
                                      {occasionLabels[list.occasion] || list.occasion}
                                    </span>
                                  )}
                                  <span className="text-surface-500">
                                    {list.gift_count} {list.gift_count === 1 ? 'gift' : 'gifts'}
                                  </span>
                                  {!group.isCurrentUser && !list.is_shared && (
                                    <span className="text-amber-400">
                                      {list.gift_count - list.reserved_count} available
                                    </span>
                                  )}
                                  {list.is_shared && (
                                    <span className="text-amber-400">
                                      {list.gift_count - list.reserved_count} available
                                    </span>
                                  )}
                                  {group.isCurrentUser && (
                                    <span className="text-emerald-400">
                                      {list.reserved_count} reserved
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <ArrowRightIcon className="w-4 h-4 text-surface-500 group-hover:text-primary-400 transition-colors" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Create Wishlist</h2>
                <button onClick={() => setShowCreateModal(false)} className="btn-icon">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                {/* Owner Selection */}
                <div className="input-group">
                  <label className="input-label text-xs">Create for</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setValue('owner_type', 'self')}
                      className={`p-2 rounded-lg border-2 transition-all text-left ${
                        ownerType === 'self'
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-surface-600 hover:border-surface-500'
                      }`}
                    >
                      <UserIcon className={`w-4 h-4 mb-0.5 ${ownerType === 'self' ? 'text-primary-400' : 'text-surface-400'}`} />
                      <div className={`text-sm font-medium ${ownerType === 'self' ? 'text-white' : 'text-surface-300'}`}>
                        Myself
                      </div>
                    </button>
                    {managedMembers.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setValue('owner_type', 'member')}
                        className={`p-2 rounded-lg border-2 transition-all text-left ${
                          ownerType === 'member'
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-600 hover:border-surface-500'
                        }`}
                      >
                        <UserGroupIcon className={`w-4 h-4 mb-0.5 ${ownerType === 'member' ? 'text-primary-400' : 'text-surface-400'}`} />
                        <div className={`text-sm font-medium ${ownerType === 'member' ? 'text-white' : 'text-surface-300'}`}>
                          Family Member
                        </div>
                      </button>
                    )}
                  </div>
                </div>

                {/* Member Selection (if creating for someone else) */}
                {ownerType === 'member' && managedMembers.length > 0 && (
                  <div className="input-group">
                    <label className="input-label text-xs">Select Member</label>
                    <select className="input text-sm" {...register('owner_member_id', { required: ownerType === 'member' })}>
                      <option value="">Choose a family member...</option>
                      {managedMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.first_name} {member.last_name || ''} {member.nickname ? `(${member.nickname})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="input-group">
                  <label className="input-label text-xs">List Name *</label>
                  <input
                    type="text"
                    className="input text-sm"
                    placeholder="e.g., Birthday Wishlist"
                    {...register('name', { required: true })}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label text-xs">Description</label>
                  <textarea
                    className="input text-sm"
                    rows={2}
                    placeholder="Optional description..."
                    {...register('description')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label text-xs">Occasion</label>
                  <select className="input text-sm" {...register('occasion')}>
                    <option value="">Select occasion...</option>
                    {Object.entries(occasionLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary flex-1 text-sm py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="btn-primary flex-1 text-sm py-2"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create List'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Edit Wishlist</h2>
                <button onClick={() => setShowEditModal(null)} className="btn-icon">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitEdit(onSubmitEdit)} className="space-y-3">
                <div className="input-group">
                  <label className="input-label text-xs">List Name *</label>
                  <input
                    type="text"
                    className="input text-sm"
                    placeholder="e.g., Birthday Wishlist"
                    {...registerEdit('name', { required: true })}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label text-xs">Description</label>
                  <textarea
                    className="input text-sm"
                    rows={2}
                    placeholder="Optional description..."
                    {...registerEdit('description')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label text-xs">Occasion</label>
                  <select className="input text-sm" {...registerEdit('occasion')}>
                    <option value="">Select occasion...</option>
                    {Object.entries(occasionLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(null)}
                    className="btn-secondary flex-1 text-sm py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="btn-primary flex-1 text-sm py-2"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-surface-800 rounded-xl p-4 w-full max-w-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Share Wishlist</h2>
                <button onClick={() => setShowShareModal(null)} className="btn-icon p-1">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {loadingLinkedFamilies ? (
                <div className="flex justify-center py-6">
                  <div className="w-6 h-6 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : linkedFamilies.length === 0 ? (
                <div className="text-center py-6">
                  <UsersIcon className="w-10 h-10 mx-auto mb-2 text-surface-500" />
                  <p className="text-surface-400 text-sm mb-1">No linked families</p>
                  <p className="text-surface-500 text-xs">
                    Link with another family first to share wishlists
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-surface-300 text-sm mb-3">
                    Select a linked family to share this wishlist with:
                  </p>
                  {linkedFamilies.map((family) => (
                    <button
                      key={family.id}
                      onClick={() => handleShareList(family.family_id)}
                      className="w-full p-3 rounded-lg bg-surface-700 hover:bg-surface-600 transition-colors flex items-center gap-2.5 text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center">
                        <UsersIcon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-white">{family.family_name}</h3>
                        <p className="text-xs text-surface-400">Click to share</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
